"use client"
import "./FieldMappingVisualizer.css"

const FieldMappingVisualizer = ({ patientData, mappedData, mappingPaths, activeField, onFieldSelect }) => {
  if (!mappedData || !mappingPaths) {
    return <div className="no-data">No mapping data available</div>
  }

  // Group fields by category
  const fieldGroups = {
    Demographics: ["AHCno", "Id", "Name", "Age", "Gender"],
    "Physical Measurements": [
      "Height",
      "Weight",
      "BMI",
      "BloodPressureSystolic",
      "BloodPressureDiastolic",
      "HeartRatePerMinute",
      "RespirationRatePerMinute",
      "Rhythm",
    ],
    "Medical Conditions": [
      "DiabetesMellitus",
      "Hypertension",
      "Dyslipidaemia",
      "PrevHeartDisease",
      "HeartRelatedSymptom",
      "FamilyHistoryofHeartDisease",
    ],
    "Lifestyle Factors": ["PhysicalActivity", "Smoke", "Tobacco", "Diet", "Alcohol"],
  }

  const generateTransformationSteps = (field, sourceValue) => {
    const steps = []

    switch (field) {
      case "DiabetesMellitus":
        steps.push(
          "Problem List Section: If the patient has been diagnosed with diabetes, it is likely to be listed here.",
        )
        steps.push(
          "Medication Administration Records (MAR) Section: If the patient has been prescribed insulin or other diabetes-related medications, those would be listed here.",
        )
        steps.push(
          "Lab Results Section: Blood glucose levels and other lab results related to diabetes would be found in this section.",
        )
        steps.push("Flow Sheets: These are used for tracking chronic conditions like diabetes over time.")
        steps.push(
          "Visit Diagnosis Section: If diabetes was a diagnosis made during a particular visit, it would be mentioned here.",
        )
        steps.push(
          "Progress Notes Section: Doctors or nurses might document the patient's diabetic symptoms, treatment responses, or changes in condition in their notes.",
        )
        steps.push(
          "Clinical Notes Section: Any notes or observations made by the clinical team about the patient's diabetes would be documented here.",
        )
        steps.push(
          "Family History Section: A family history of diabetes might be noted here, given that diabetes can have a genetic component.",
        )
        break
      case "Dyslipidaemia":
        steps.push(
          "Problem List Section: If the patient has been diagnosed with dyslipidaemia, it is likely to be listed here.",
        )
        steps.push(
          "Medication Administration Records (MAR) Section: If the patient has been prescribed statins or other lipid-lowering medications, those would be listed here.",
        )
        steps.push(
          "Lab Results Section: Lipid panel results, including cholesterol and triglyceride levels, would be found in this section.",
        )
        steps.push("Flow Sheets: These are used for tracking chronic conditions like dyslipidaemia over time.")
        steps.push(
          "Visit Diagnosis Section: If dyslipidaemia was a diagnosis made during a particular visit, it would be mentioned here.",
        )
        steps.push(
          "Progress Notes Section: Doctors or nurses might document the patient's dyslipidaemia symptoms, treatment responses, or changes in condition in their notes.",
        )
        steps.push(
          "Clinical Notes Section: Any notes or observations made by the clinical team about the patient's dyslipidaemia would be documented here.",
        )
        steps.push(
          "Family History Section: A family history of dyslipidaemia might be noted here, given that it can have a genetic component.",
        )
        break
      case "Hypertension":
        steps.push(
          "Problem List Section: If the patient has been diagnosed with hypertension, it is likely to be listed here.",
        )
        steps.push(
          "Vital Signs Section: This section often includes blood pressure readings, which are key indicators of hypertension.",
        )
        steps.push(
          "Medication Administration Records (MAR) Section: If the patient has been prescribed antihypertensive medications, those would be listed here.",
        )
        steps.push("Flow Sheets: These are used for tracking chronic conditions like hypertension over time.")
        steps.push(
          "Visit Diagnosis Section: If hypertension was a diagnosis made during a particular visit, it would be mentioned here.",
        )
        steps.push(
          "Progress Notes Section: Doctors or nurses might document the patient's hypertensive symptoms, treatment responses, or changes in condition in their notes.",
        )
        steps.push(
          "Clinical Notes Section: Any notes or observations made by the clinical team about the patient's hypertension would be documented here.",
        )
        break
      case "Height":
        steps.push(
          "Vital Signs Section: Height is often recorded in the Vital Signs section of a patient's chart, especially if it is taken during a physical examination.",
        )
        steps.push(
          "Patient Summary: The Patient Summary or Snapshot can also include a section for vital signs, including height.",
        )
        steps.push(
          "Growth Charts: For pediatric patients, height is often tracked over time in a growth chart, which can be found in the patient's chart.",
        )
        steps.push(
          "Encounter or Visit Information: Height may be recorded as part of the patient's encounter or visit information, particularly if the visit involves a physical examination.",
        )
        steps.push(
          "Health Maintenance: In some cases, there may be a record of height under the health maintenance section of the patient's chart.",
        )
        steps.push(
          "Flowsheets: Some organizations may use flowsheets to record and track patient height, particularly for patients who are hospitalized or in long-term care.",
        )
        break
      case "Weight":
        steps.push(
          "Vital Signs Section: Weight is often recorded in the Vital Signs section of a patient's chart, especially if it is taken during a physical examination.",
        )
        steps.push(
          "Patient Summary: The Patient Summary or Snapshot can also include a section for vital signs, including weight.",
        )
        steps.push(
          "Growth Charts: For pediatric patients, weight is often tracked over time in a growth chart, which can be found in the patient's chart.",
        )
        steps.push(
          "Encounter or Visit Information: Weight may be recorded as part of the patient's encounter or visit information, particularly if the visit involves a physical examination.",
        )
        steps.push(
          "Health Maintenance: In some cases, there may be a record of weight under the health maintenance section of the patient's chart.",
        )
        steps.push(
          "Flowsheets: Some organizations may use flowsheets to record and track patient weight, particularly for patients who are hospitalized or in long-term care.",
        )
        break
      default:
        steps.push("Extract value from patient data using the specified path")
        steps.push("Apply any necessary transformations or validations")
        steps.push("Return the final mapped value")
    }

    return steps
  }

  // Get source value based on mapping path
  const getSourceValue = (field) => {
    const path = mappingPaths[field]
    if (!path) return "Not found"

    // Handle calculated or complex paths
    if (path.includes("calculated") || path.includes("elevated") || path.includes("found")) {
      return `Derived value (${path})`
    }

    // Special handling for Height and Weight to extract direct values
    if (field === "Height" && patientData.flowsheets?.height) {
      return patientData.flowsheets.height.value
    }

    if (field === "Weight" && patientData.flowsheets?.weight) {
      return patientData.flowsheets.weight.value
    }

    // Special handling for DiabetesMellitus
    if (field === "DiabetesMellitus" && path.includes("visit_diagnosis>diagnoses")) {
      if (patientData.visit_diagnosis?.diagnoses) {
        const diabetesDiagnosis = patientData.visit_diagnosis.diagnoses.find(
          (diagnosis) => diagnosis.name === "Diabetes Mellitus",
        )
        if (diabetesDiagnosis) {
          return diabetesDiagnosis.status
        }
      }
    }

    // Special handling for Dyslipidaemia
    if (field === "Dyslipidaemia") {
      if (path.includes("visit_diagnosis>diagnoses")) {
        if (patientData.visit_diagnosis?.diagnoses) {
          const dyslipidemiaDiagnosis = patientData.visit_diagnosis.diagnoses.find(
            (diagnosis) => diagnosis.name === "Dyslipidemia",
          )
          if (dyslipidemiaDiagnosis) {
            return dyslipidemiaDiagnosis.status
          }
        }
      }

      if (path.includes("medication_administration_records")) {
        if (patientData.medication_administration_records?.Dyslipidemia) {
          return patientData.medication_administration_records.Dyslipidemia
        }
      }
    }

    // Special handling for Hypertension
    if (field === "Hypertension" && path.includes("problem_list>Hypertension>status")) {
      if (patientData.problem_list?.Hypertension) {
        return patientData.problem_list.Hypertension.status
      }
    }

    // Handle paths with special patterns
    if (path.includes(">diagnoses>")) {
      const [section, subsection, diagnosisName] = path.split(">")
      if (patientData[section]?.[subsection]) {
        const diagnosis = patientData[section][subsection].find((d) => d.name === diagnosisName)
        return diagnosis ? diagnosis.status : "Diagnosis not found"
      }
    }

    // Handle medication administration records
    if (path.includes("medication_administration_records")) {
      const parts = path.split(">")
      const medicationType = parts[1]
      if (patientData.medication_administration_records?.[medicationType]) {
        return patientData.medication_administration_records[medicationType]
      }
    }

    // Handle problem list with status
    if (path.includes("problem_list") && path.includes("status")) {
      const parts = path.split(">")
      const problemName = parts[1]
      if (patientData.problem_list?.[problemName]) {
        return patientData.problem_list[problemName].status
      }
    }

    // Handle simple paths
    const pathParts = path.split(">")
    let value = patientData
    for (const part of pathParts) {
      if (value && value[part] !== undefined) {
        value = value[part]
      } else {
        return "Path not found in data"
      }
    }

    // Handle object values
    if (value && typeof value === "object" && !Array.isArray(value)) {
      if (value.value !== undefined) {
        return value.value
      }
      if (value.status !== undefined) {
        return value.status
      }
      return JSON.stringify(value)
    }

    return value
  }

  return (
    <div className="field-mapping-container">
      <div className="field-list">
        {Object.entries(fieldGroups).map(([groupName, fields]) => (
          <div key={groupName} className="field-group">
            <h3 className="field-group-title">{groupName}</h3>
            <ul className="field-group-list">
              {fields.map((field) => (
                <li
                  key={field}
                  className={`field-item ${activeField === field ? "active" : ""}`}
                  onClick={() => onFieldSelect(field)}
                >
                  <span className="field-item-name">{field}</span>
                  <span className="field-item-value">{mappedData[field] || "N/A"}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="field-detail">
        {activeField && (
          <>
            <div className="field-detail-header">
              <h3 className="field-detail-title">{activeField}</h3>
              <div className="field-detail-value">{mappedData[activeField] || "N/A"}</div>
            </div>

            <div className="field-detail-content">
              <div className="field-detail-section">
                <h4 className="field-detail-section-title">Source Path</h4>
                <div className="field-detail-path">{mappingPaths[activeField] || "Not found"}</div>
              </div>

              <div className="field-detail-section">
                <h4 className="field-detail-section-title">Source Value</h4>
                <div className="field-detail-source">{getSourceValue(activeField) || "N/A"}</div>
              </div>

              <div className="field-detail-section">
                <h4 className="field-detail-section-title">EPIC Field Mapping Process</h4>
                <ol className="field-detail-steps">
                  {generateTransformationSteps(activeField, getSourceValue(activeField)).map((step, index) => (
                    <li key={index} className="field-detail-step">
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default FieldMappingVisualizer
